import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "react-modal";
import "./GiftEntries.css";
import { useAuth } from "../AuthContext";
import { debounce } from "lodash";

Modal.setAppElement("#root");

function GiftEntries() {
  const { mobile } = useAuth();
  const [gifts, setGifts] = useState([]);
  const [giftEntries, setGiftEntries] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [qtyModalIsOpen, setQtyModalIsOpen] = useState(false);
  const [selectedGift, setSelectedGift] = useState({
    gift_id: "",
    gift_name: "",
    customer_name: "",
    qty: 1, // Set default quantity to 1
    date: new Date().toISOString().split("T")[0], // Set default date to today
  });
  const [quantity, setQuantity] = useState(0);
  const [newGiftName, setNewGiftName] = useState("");
  const [customerNames, setCustomerNames] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [giftStockModalIsOpen, setGiftStockModalIsOpen] = useState(false);
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [debouncedCustomerName, setDebouncedCustomerName] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [filteredCustomerNames, setFilteredCustomerNames] = useState([]);

  useEffect(() => {
    fetchGifts();
    fetchGiftEntries();
    fetchCustomerNames();
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setSelectedGift({
        ...selectedGift,
        customer_name: debouncedCustomerName,
      });
    }, 1); // Adjust debounce delay as needed
    return () => clearTimeout(handle);
  }, [debouncedCustomerName]);

  useEffect(() => {
    setFilteredEntries(
      giftEntries.filter(
        (entry) =>
          entry.customer_name
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          gifts
            .find((gift) => gift.id === entry.gift_id)
            ?.gift_name.toLowerCase()
            .includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, giftEntries, gifts]);

  const fetchGifts = async () => {
    try {
      const result = await axios(
        `${process.env.REACT_APP_API_URL}/api/gifts_store`
      );
      const sortedGifts = result.data.sort((a, b) =>
        a.gift_name.localeCompare(b.gift_name)
      );
      setGifts(sortedGifts);
      if (sortedGifts.length === 1) {
        // Automatically set the single gift as selected if only one is available
        setSelectedGift({
          gift_id: sortedGifts[0].id,
          gift_name: sortedGifts[0].gift_name,
          qty: "",
          date: "",
        });
      }
    } catch (error) {
      console.error("Failed to fetch gifts", error);
    }
  };
  const fetchGiftEntries = async () => {
    try {
      const result = await axios(
        `${process.env.REACT_APP_API_URL}/api/gift-entries`
      );
      const sortedEntries = result.data.sort((a, b) => b.id - a.id);
      setGiftEntries(sortedEntries);
    } catch (error) {
      console.error("Failed to fetch gift entries", error);
    }
  };

  const fetchCustomerNames = async () => {
    const result = await axios(
      `${process.env.REACT_APP_API_URL}/api/customers-names`
    );
    setCustomerNames(result.data);
  };

  const handleOpenModal = () => {
    setSelectedGift({
      gift_id: "",
      gift_name: "",
      customer_name: "",
      qty: 1, // Set default quantity to 1
      date: new Date().toISOString().split("T")[0], // Set default date to today
    });
    setModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
  };

  const handleOpenQtyModal = () => {
    setQtyModalIsOpen(true);
  };

  const handleCloseQtyModal = () => {
    setQtyModalIsOpen(false);
  };

  const handleViewGiftStock = () => {
    setGiftStockModalIsOpen(true);
  };

  const handleCloseGiftStockModal = () => {
    setGiftStockModalIsOpen(false);
  };

  const handleAddGiftEntry = async (event) => {
    event.preventDefault();
    if (isSubmitting) return; // Prevent further action if already submitting
    setIsSubmitting(true); // Disable further submissions
    try {
      const giftData = gifts.find((gift) => gift.id == selectedGift.gift_id);
      if (giftData && giftData.qty >= selectedGift.qty) {
        // Gift quantity is sufficient
        await axios.post(`${process.env.REACT_APP_API_URL}/api/gift-entries`, {
          gift_id: selectedGift.gift_id,
          customer_name: selectedGift.customer_name,
          qty: selectedGift.qty,
          date: selectedGift.date,
        });

        // Update the gift quantity in the frontend
        const updatedGifts = gifts.map((gift) =>
          gift.id == selectedGift.gift_id
            ? { ...gift, qty: gift.qty - selectedGift.qty }
            : gift
        );
        setGifts(updatedGifts);

        // Update the gift quantity in the database
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/gifts_store/${selectedGift.gift_id}`,
          {
            qty: giftData.qty - selectedGift.qty,
          }
        );

        fetchGiftEntries();
        // handleCloseModal();
      } else {
        // Gift quantity is insufficient
        alert("Insufficient gift quantity");
      }
    } catch (error) {
      console.error("Failed to add gift entry", error);
    } finally {
      setIsSubmitting(false); // Re-enable the button after processing
      handleCloseModal(); // Optionally close modal on success or error
      setSelectedGift({
        gift_id: "",
        gift_name: "",
        customer_name: "",
        qty: 1,
        date: new Date().toISOString().split("T")[0],
      });
    }
  };

  const handleGiftSelect = (e) => {
    const giftId = e.target.value;

    const selectedGiftData = gifts.find((gift) => gift.id == giftId);
    if (selectedGiftData) {
      setSelectedGift({
        ...selectedGift,
        gift_id: giftId,
        gift_name: selectedGiftData.gift_name,
      });
    } else {
      setSelectedGift({
        ...selectedGift,
        gift_id: "",
        gift_name: "",
      });
    }
  };

  const handleGiftChangeAndUpdateQty = (e) => {
    const giftId = e.target.value;
    const selectedGiftData = gifts.find((gift) => gift.id === giftId);
    if (selectedGiftData) {
      setNewGiftName(selectedGiftData.gift_name);
      setQuantity(selectedGiftData.qty);
      setSelectedGift({
        ...selectedGiftData,
        isNew: false,
        qty: selectedGiftData.qty,
      });
    } else {
      setNewGiftName("");
      setQuantity(0);
      setSelectedGift({ isNew: true, qty: 0 });
    }
  };

  const handleNewGiftNameChange = (e) => {
    const selectedGiftData = gifts.find(
      (gift) => gift.gift_name === e.target.value
    );
    if (selectedGiftData) {
      setNewGiftName(selectedGiftData.gift_name);
      setQuantity(selectedGiftData.qty);
      setSelectedGift({ ...selectedGiftData, isNew: false });
    } else {
      setNewGiftName(e.target.value);
      setQuantity(0);
      setSelectedGift({ isNew: true });
    }
  };

  const handleCustomerNameChange = (e) => {
    // console.log("entered");
    const searchValue = e.target.value;
    setDebouncedCustomerName(searchValue);

    // Filter the customer names based on input
    const filtered = customerNames
      .filter((customer) =>
        customer.customer_name.toLowerCase().includes(searchValue.toLowerCase())
      )
      .slice(0, 10); // Limit to 10 results

    setFilteredCustomerNames(filtered);

    // Show dropdown if there are matching results
    setIsDropdownVisible(filtered.length > 0);
    // console.log("left");
  };

  const handleCustomerSelect = (name) => {
    // Set selected customer and hide dropdown
    setDebouncedCustomerName(name);
    setSelectedGift({ ...selectedGift, customer_name: name });
    setIsDropdownVisible(false);
  };

  const handleUpdateGiftQty = async (event) => {
    event.preventDefault();
    if (isSubmitting) return; // Prevent further action if already submitting
    setIsSubmitting(true); // Disable further submissions
    try {
      if (code !== "5037") {
        alert("Incorrect security code.");
        setIsSubmitting(false);
        return; // Stop the function if the code is incorrect
      }

      if (selectedGift.isNew) {
        // Add new gift
        const result = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/gifts_store`,
          {
            gift_name: newGiftName,
            qty: quantity,
          }
        );
        const newGift = result.data;
        // Add entry to gift_entries table
        await axios.post(`${process.env.REACT_APP_API_URL}/api/gift-entries`, {
          gift_id: newGift.id,
          customer_name: "admin",
          qty: quantity,
          date: new Date().toISOString().split("T")[0], // Use current date
        });
      } else {
        // Update existing gift's quantity
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/gifts_store/${selectedGift.id}`,
          {
            qty: quantity,
          }
        );
        // Add entry to gift_entries table
        await axios.post(`${process.env.REACT_APP_API_URL}/api/gift-entries`, {
          gift_id: selectedGift.id,
          customer_name: "admin",
          qty: quantity, // Store the updated quantity difference
          date: new Date().toISOString().split("T")[0], // Use current date
        });
      }
      fetchGifts(); // Refresh the gifts list
      fetchGiftEntries(); // Refresh the gift entries list
    } catch (error) {
      console.error("Failed to update gift quantity", error);
    } finally {
      setIsSubmitting(false); // Re-enable the button after processing
      setNewGiftName(""); // Reset newGiftName
      setQuantity(0); // Reset quantity
      setCode("");
      setSelectedGift({}); // Reset selectedGift
      handleCloseQtyModal(); // Close the modal
    }
  };

  const GiftStockModalContent = ({ gifts }) => (
    <div className="modal-content-gifts">
      <table className="modal-table">
        <thead>
          <tr>
            <th>Gift Name</th>
            <th>Quantity</th>
          </tr>
        </thead>
        <tbody>
          {gifts.map((gift) => (
            <tr key={gift.id}>
              <td>{gift.gift_name}</td>
              <td>{gift.qty}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <div className="gift-entries">
      <h1>Gift Management</h1>
      <button onClick={handleViewGiftStock} className="view-gift-stock-btn">
        View Gifts Stock
      </button>
      <div className="top-controls">
        <input
          type="text"
          placeholder="Search by Gift or Customer..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
        <div>
          {mobile === "8725092149" && (
            <button onClick={handleOpenQtyModal}>Add / Update Gift</button>
          )}
          {mobile === "8725092149" && (
            <button onClick={handleOpenModal}>Add New Gift Entry</button>
          )}
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Gift Name</th>
            <th>Customer Name</th>
            <th>Quantity</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {filteredEntries.map((entry) => (
            <tr
              key={entry.id}
              className={entry.customer_name === "admin" ? "admin-entry" : ""}
            >
              <td>
                {gifts.find((gift) => gift.id == entry.gift_id)?.gift_name ||
                  "Unknown Gift"}
              </td>
              <td>{entry.customer_name}</td>
              <td>{entry.qty}</td>
              <td>
                {new Date(entry.date).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Gift Entry Modal"
        className="modal"
        overlayClassName="overlay"
      >
        <h2>Add Gift Entry</h2>
        <form onSubmit={handleAddGiftEntry}>
          <label>
            Gift Name:
            <select
              value={selectedGift?.gift_id || ""}
              onChange={handleGiftSelect} // Updated to use handleGiftSelect
              required
            >
              <option value="">Select a gift</option>
              {gifts.map((gift) => (
                <option key={gift.id} value={gift.id}>
                  {gift.gift_name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Customer Name:
            {/* <input
              list="customer-names"
              // value={selectedGift?.customer_name || ""}
              // onChange={(e) =>
              //   setSelectedGift({
              //     ...selectedGift,
              //     customer_name: e.target.value,
              //   })
              // }
              value={debouncedCustomerName || ""}
              onChange={(e) => setDebouncedCustomerName(e.target.value)}
              required
            /> */}
            {/* <datalist id="customer-names">
              {customerNames.map((customer) => (
                <option key={customer.id} value={customer.customer_name} />
              ))}
            </datalist> */}
            <input
              type="text"
              value={debouncedCustomerName}
              onChange={handleCustomerNameChange}
              onFocus={() => setIsDropdownVisible(true)} // Show dropdown when input is focused
              required
            />
            {/* <datalist id="customer-names">
              {customerNames
                .filter((customer) =>
                  customer.customer_name
                    .toLowerCase()
                    .includes(debouncedCustomerName.toLowerCase())
                )
                .slice(0, 10) // Show only the top 10 matches
                .map((customer) => (
                  <option key={customer.id} value={customer.customer_name} />
                ))}
            </datalist> */}
          </label>
          {isDropdownVisible && (
            <ul className="dropdown-list">
              {filteredCustomerNames.map((customer) => (
                <li
                  key={customer.id}
                  onClick={() => handleCustomerSelect(customer.customer_name)}
                  className="dropdown-item"
                >
                  {customer.customer_name}
                </li>
              ))}
            </ul>
          )}

          <label>
            Quantity:
            <input
              type="number"
              value={selectedGift?.qty || ""}
              onChange={(e) =>
                setSelectedGift({ ...selectedGift, qty: e.target.value })
              }
              required
            />
          </label>
          <label>
            Date:
            <input
              type="date"
              value={selectedGift?.date || ""}
              onChange={(e) =>
                setSelectedGift({ ...selectedGift, date: e.target.value })
              }
              required
            />
          </label>
          <button type="submit" disabled={isSubmitting}>
            Add
          </button>
          <button onClick={handleCloseModal} type="button">
            Cancel
          </button>
        </form>
      </Modal>
      <Modal
        isOpen={qtyModalIsOpen}
        onRequestClose={handleCloseQtyModal}
        contentLabel="Manage Gift Quantity"
        className="modal"
        overlayClassName="overlay"
      >
        <h2>Add New Gift / Update Qty </h2>
        <form onSubmit={handleUpdateGiftQty}>
          <label>
            Gift Name (select or type new):
            <input
              type="text"
              value={newGiftName}
              onChange={handleNewGiftNameChange}
              placeholder="Type or select a gift name"
              list="gift-names"
              required
            />
            <datalist id="gift-names">
              {gifts.map((gift) => (
                <option key={gift.id} value={gift.gift_name} />
              ))}
            </datalist>
          </label>
          <label>
            Current Quantity:
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value, 10))}
              min="0"
              required
            />
          </label>
          <label>
            Security Code:
            <input
              type="password"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength="4"
              placeholder="Enter 4-digit code"
              required
              pattern="\d{4}" // This pattern ensures that only four digits are entered
              title="Please enter a 4-digit code"
            />
          </label>
          <button type="submit" disabled={isSubmitting}>
            Add Gift / Update Quantity
          </button>
          <button onClick={handleCloseQtyModal} type="button">
            Cancel
          </button>
        </form>
      </Modal>
      <Modal
        isOpen={giftStockModalIsOpen}
        onRequestClose={handleCloseGiftStockModal}
        contentLabel="Gift Stock Modal"
        className="modal"
        overlayClassName="overlay"
      >
        <h2>Gifts Stock</h2>
        <GiftStockModalContent gifts={gifts} />
        <button onClick={handleCloseGiftStockModal}>Close</button>
      </Modal>
    </div>
  );
}

export default GiftEntries;
